import { useMemo, useState } from 'react';

import useSetApproveAgreements from '@/hooks/qna/mobile/Write/useSetApproveAgreements';
import useAgreementsForAskQuestionCategory from '@/hooks/qna/useAgreementsForAskQuestionCategory';
import useGetCheckApprovedByCategory from '@/hooks/qna/useGetCheckApprovedByCategory';
import useGetCheckApprovedByType from '@/hooks/qna/useGetCheckApprovedByType';
import { AgreementCategory, AgreementType } from '@/types/bff/gql-schema';
import { Policy } from '@/types/qna';
import {
  NATIVE_POST_MESSAGE_TYPE,
  isWebView,
  sendPostMessageToNative,
} from '@/utils/mobile/RNPlugins';

export interface PolicyAgreementBottomSheetViewModel {
  policyList: Array<Policy>;
  checkedPolicyList: Array<Policy>;
  checkPolicy: (policy: Policy) => void;
  checkAll: () => void;
  agreeToPolicies: () => Promise<void>;
  isAllChecked: () => boolean;
  isAllRequiredChecked: () => boolean;
  isPolicyChecked: (policy: Policy) => boolean;
  hasAlreadyAgreedToAll: () => boolean;
  hasAgreedToMinimum: () => boolean;
  hasAgreedToLocation: () => boolean;
  isBottomSheetOpened: () => boolean;
  openBottomSheet: () => void;
  closeBottomSheet: () => void;
  openPolicyDetail: (policy: Policy) => void;
}

const usePolicyAgreementBottomSheetViewModel =
  (): PolicyAgreementBottomSheetViewModel => {
    const { data: _policyList } = useAgreementsForAskQuestionCategory();
    const { data: hasAlreadyAgreedToAll } = useGetCheckApprovedByCategory({
      category: AgreementCategory.QnaAgreement,
    });
    const { data: hasAgreedToHealthInformation } = useGetCheckApprovedByType({
      category: AgreementCategory.QnaAgreement,
      type: AgreementType.CollectHealthInformation,
    });
    const { data: hasAgreedToLocation } = useGetCheckApprovedByType({
      category: AgreementCategory.QnaAgreement,
      type: AgreementType.LocationBasedServiceTermsAndConditions,
    });
    const { setApproveAgreementMutate } = useSetApproveAgreements();
    const [checkedPolicyIdList, setCheckedPolicyIdList] = useState<
      Array<string>
    >([]);
    const [
      isPolicyAgreementBottomSheetOpened,
      setIsPolicyAgreementBottomSheetOpened,
    ] = useState<boolean>(false);

    const policyList = useMemo(
      () =>
        _policyList.filter((policy) => {
          return !(
            policy.type ===
              AgreementType.LocationBasedServiceTermsAndConditions &&
            hasAgreedToLocation?.result
          );
        }),
      [_policyList],
    );

    const openPolicyAgreementBottomSheet = () => {
      setIsPolicyAgreementBottomSheetOpened(true);
    };

    const closePolicyAgreementBottomSheet = () => {
      setIsPolicyAgreementBottomSheetOpened(false);
    };

    const checkedPolicyList = useMemo(() => {
      return policyList.filter((policy) =>
        checkedPolicyIdList.includes(policy.id),
      );
    }, [checkedPolicyIdList, policyList]);

    const checkAll = () => {
      if (checkedPolicyIdList.length === policyList.length) {
        setCheckedPolicyIdList([]);
      } else {
        setCheckedPolicyIdList(policyList.map((policy) => policy.id));
      }
    };
    const checkPolicy = (policy: Policy) => {
      if (checkedPolicyIdList.includes(policy.id)) {
        setCheckedPolicyIdList((_prev) =>
          _prev.filter((id) => id !== policy.id),
        );
      } else {
        setCheckedPolicyIdList((_prev) => [..._prev, policy.id]);
      }
    };

    const isAllChecked = () => {
      if (policyList.length === 0) {
        return false;
      }
      return Boolean(checkedPolicyIdList.length === policyList.length);
    };

    const isAllRequiredChecked = () => {
      if (policyList.length === 0) {
        return false;
      }

      const requiredList = policyList.filter((policy) => policy.required);

      return (
        requiredList.length === 0 ||
        requiredList.every((policy) => checkedPolicyIdList.includes(policy.id))
      );
    };

    const isPolicyChecked = (policy: Policy) => {
      return checkedPolicyIdList.includes(policy.id);
    };

    const agreeToPolicies = async () => {
      await setApproveAgreementMutate({
        request: {
          approvedAgreementIds: [
            ...checkedPolicyIdList.map((id) => Number(id)),
          ],
          disapprovedAgreementIds: [],
        },
      });
    };

    const hasAgreedToMinimum = Boolean(
      hasAgreedToLocation?.result && hasAgreedToHealthInformation?.result,
    );

    const openPolicyDetail = (policy: Policy) => {
      if (isWebView()) {
        return sendPostMessageToNative(
          NATIVE_POST_MESSAGE_TYPE.OPEN_EXTERNAL_BROWSER,
          {
            url: policy.url as string,
          },
        );
      }
      window.open(policy.url as string, '_blank');
    };

    return {
      policyList: policyList,
      checkedPolicyList: checkedPolicyList,
      checkPolicy: checkPolicy,
      checkAll: checkAll,
      isAllChecked: isAllChecked,
      isAllRequiredChecked: isAllRequiredChecked,
      isPolicyChecked: isPolicyChecked,
      hasAlreadyAgreedToAll: () => Boolean(hasAlreadyAgreedToAll),
      hasAgreedToMinimum: () => hasAgreedToMinimum,
      hasAgreedToLocation: () => Boolean(hasAgreedToLocation?.result),
      agreeToPolicies: agreeToPolicies,
      isBottomSheetOpened: () => Boolean(isPolicyAgreementBottomSheetOpened),
      openBottomSheet: openPolicyAgreementBottomSheet,
      closeBottomSheet: closePolicyAgreementBottomSheet,
      openPolicyDetail: openPolicyDetail,
    };
  };

export default usePolicyAgreementBottomSheetViewModel;
